import {keepPreviousData, queryOptions, useQuery} from "@tanstack/react-query";
import {DateRange} from "react-day-picker";

import * as apiClient from "@/lib/api";
import {useQueryResetWithRefresh} from "@/lib/hooks/query-refetch-with-reset";
import {buildQueryParamsStringWithFilters} from "@/lib/utils";
import {ReviewDataQueryResponse} from "@/modules/reviews/types";
import {useDate} from "@/stores/date-filter";
import {FetchParams} from "@/types/search";
import {QUERY_KEY_REVIEW_DATA} from "@/utils/keys";

function fetchReviewData(date: DateRange, fetchParams: FetchParams): Promise<ReviewDataQueryResponse> {
  return apiClient.get(`/firm/review-data?${buildQueryParamsStringWithFilters(fetchParams, date, null)}`, true)
}

export const reviewDataQueryOptions = (
  date: DateRange,
  fetchParams: FetchParams
) =>
  queryOptions({
    queryKey: [QUERY_KEY_REVIEW_DATA, date, fetchParams],
    queryFn: () => fetchReviewData(date, fetchParams),
    placeholderData: keepPreviousData
  });

export const useReviewData = (fetchParams: FetchParams) => {
  const { date } = useDate()
  const queryOptions = reviewDataQueryOptions(date, fetchParams)
  const query = useQuery(queryOptions)
  const { resetAndRefetch } = useQueryResetWithRefresh(queryOptions.queryKey, query.refetch)

  return {
    data: query.data?.data ?? [],
    total: query.data?.aggregate_data?.total ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    isRefetchError: query.isRefetchError,
    error: query.error,
    isRefetching: query.isRefetching,
    isSuccess: query.isSuccess,
    status: query.status,
    refetch: resetAndRefetch
  }
}